import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import MembershipService from '@xo-union/sdk-membership';
import {CookieStorage} from 'cookie-storage';
import HeaderNav from '@xo-union/tk-component-header-nav/remote';
import MembershipModal from '@xo-union/tk-component-membership-modal';
import sha1 from 'crypto-js/sha1';
import useIdentifyMember from './hooks/useIdentifyMember';

const isProd = process.env.ENV === 'production';
const sessionToken = new CookieStorage().getItem('xo-session-token');
const membershipService = new MembershipService({
  clientOptions: {
    env: isProd ? 'production' : 'qa',
    apiKey: process.env.MEMBERSHIP_API_KEY,
    version: 'v2',
  },
});
const SSO_APPLICATION_NAME = isProd ? 'registry' : 'test-registry';
const SSO_PROVIDER = 'apple';
const SSO_ERRORS_TO_SUPPRESS = [
  'popup_closed_by_user',
  'user_trigger_new_signin_flow',
];

const MODAL_TYPES = {
  SIGN_UP: 'SIGN_UP',
  LOG_IN: 'LOG_IN',
};

const ERROR_MESSAGES = {
  DEFAULT: 'Oops, something went wrong.',
};

const HeaderNavWithMembership = ({headerProps}) => {
  const membershipModalRef = useRef(null);

  const [couplePhotoUrl, setCouplePhotoUrl] = useState('');
  const [isInProgress, setIsInProgress] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [openModal, setOpenModal] = useState('');
  const [customer, setCustomer] = useState({
    id: '',
    email: '',
  });

  useIdentifyMember(customer);

  useEffect(() => {
    const getMember = async () => {
      setIsInProgress(true);

      const result = await membershipService.getMember({
        token: sessionToken,
      });

      if (result.data.member) {
        const {
          couple_photo_url: theCouplePhotoUrl,
          email,
          id,
        } = result.data.member;

        setCouplePhotoUrl(theCouplePhotoUrl);
        setIsLoggedIn(true);

        setCustomer({
          id,
          email: sha1(email),
        });
        setIsInProgress(false);
      }
    };
    if (sessionToken) getMember();
  }, []);

  const openSignUpModal = () => setOpenModal(MODAL_TYPES.SIGN_UP);
  const openLogInModal = () => setOpenModal(MODAL_TYPES.LOG_IN);
  const closeModal = () => setOpenModal('');

  const handleLogOut = async () => {
    await membershipService.logOut();
    setIsLoggedIn(false);
  };

  const showError = err => {
    setIsInProgress(false);
    const message = err?.userFacingError || ERROR_MESSAGES.DEFAULT;
    membershipModalRef.current.showError(message);
  };

  const handleAuth = async (e, authMethod) => {
    try {
      setIsInProgress(true);

      await membershipService[authMethod](e.data);

      setIsLoggedIn(true);
      setIsInProgress(false);
      closeModal();
    } catch (err) {
      showError(err);
    }
  };

  const handleSignUp = e => handleAuth(e, 'signUp');
  const handleLogIn = e => handleAuth(e, 'logIn');

  const handleAppleSignIn = async () => {
    const ssoArgs = {
      provider: SSO_PROVIDER,
      flowInit: {
        application: SSO_APPLICATION_NAME,
        action: openModal.replace('_', ' ').toLowerCase(),
      },
    };

    try {
      await membershipService.ssoAuthSignIn(ssoArgs);

      setIsLoggedIn(true);
      closeModal();
    } catch (err) {
      if (SSO_ERRORS_TO_SUPPRESS.includes(err.error)) {
        return;
      }

      showError(err);
    }
  };

  return (
    <>
      <HeaderNav
        {...headerProps}
        activeProduct="registry"
        couplePhotoUrl={couplePhotoUrl}
        loggedIn={isLoggedIn}
        onClickLogIn={openLogInModal}
        onClickSignUp={openSignUpModal}
        onClickLogOut={handleLogOut}
      />
      <MembershipModal
        ref={membershipModalRef}
        metadata={{
          applicationName: `${isProd ? 'Test_' : ''}Registry`,
          userAction: 'header',
        }}
        openModal={openModal || null}
        onClickLogIn={openLogInModal}
        onClickSignUp={openSignUpModal}
        onClickAppleSignIn={handleAppleSignIn}
        onClose={closeModal}
        onSignUp={handleSignUp}
        onLogIn={handleLogIn}
        inProgress={isInProgress}
      />
    </>
  );
};

HeaderNavWithMembership.propTypes = {
  headerProps: PropTypes.shape({}).isRequired,
};

export default HeaderNavWithMembership;
